import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  fetchRestaurantGlobalFees,
  updateRestaurantGlobalFees,
} from "../../../../redux/thunk";
import { UpdateGlobalSettingsPayload, FeePreview } from "../types";
import { formatCurrency, formatPercentage } from "../mockData";
import {
  validateGlobalSettings,
  ValidationError,
  getFieldError,
  hasFieldError,
} from "../utils/validation";
import LoadingSpinner from "../../../../components/elements/LoadingSpinner";
import toast from "react-hot-toast";

const GlobalSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { globalSettings, restaurantGlobalFees, loading, error } = useAppSelector((state) => state.fees);

  const [formData, setFormData] = useState<UpdateGlobalSettingsPayload>({
    defaultCommissionRate: 10,
    defaultCommissionType: 'percentage',
    defaultBaseFee: 800,
    defaultFeePerKM: 50,
    defaultFreeDistance: 1,
    defaultServiceChargeType: 'percentage',
    defaultServiceChargeValue: 5,
    transactionFee: 50,
    setupFee: 10000,
    monthlyFee: 5000,
  });

  const [preview, setPreview] = useState<FeePreview>({
    commissionRate: "15%",
    baseDeliveryFee: "₦500",
    perKMFee: "₦34",
    serviceCharge: "2.5%",
  });

  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchRestaurantGlobalFees());
  }, [dispatch]);

  useEffect(() => {
    if (restaurantGlobalFees) {
      setFormData({
        defaultCommissionRate: restaurantGlobalFees.commissionRate,
        defaultCommissionType: 'percentage',
        defaultBaseFee: restaurantGlobalFees.deliveryRate,
        defaultFeePerKM: 50,
        defaultFreeDistance: 1,
        defaultServiceChargeType: 'percentage',
        defaultServiceChargeValue: restaurantGlobalFees.serviceCharge,
        transactionFee: 50,
        setupFee: 10000,
        monthlyFee: 5000,
      });
    }
  }, [restaurantGlobalFees]);

  useEffect(() => {
    // Update preview when form data changes
    setPreview({
      commissionRate: `${formData.defaultCommissionRate}%`,
      baseDeliveryFee: formatCurrency(formData.defaultBaseFee),
      perKMFee: formatCurrency(formData.defaultFeePerKM),
      serviceCharge: `${formData.defaultServiceChargeValue}%`,
    });
  }, [formData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('Type') ? value : parseFloat(value) || 0,
    }));
  };

  const handleSubmit = async (section: 'commission' | 'delivery' | 'service') => {
    // Clear previous validation errors
    setValidationErrors([]);
    
    setIsSubmitting(true);
    
    try {
      const payload = {
        commissionRate: formData.defaultCommissionRate,
        deliveryRate: formData.defaultBaseFee,
        serviceCharge: formData.defaultServiceChargeValue,
      };
      
      const result: any = await dispatch(updateRestaurantGlobalFees(payload));
      
      if (result.error) {
        throw new Error(result.payload || `Failed to update ${section} settings`);
      }
      
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings updated successfully!`);
      setValidationErrors([]);
      // Refetch to get latest data
      dispatch(fetchRestaurantGlobalFees());
    } catch (error: any) {
      toast.error(error.message || `Failed to update ${section} settings`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && !globalSettings) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Validation Summary */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h3>
          <ul className="list-disc list-inside space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index} className="text-sm text-red-700">
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Commission Rates Section */}
      <div className="bg-white rounded-lg p-6 border">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full border">
              <span className="text-lg font-bold">%</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Commission Rates</h3>
              <p className="text-sm text-gray-500">Platform commission settings</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Default Commission Rates</label>
            <div className="flex gap-2">
              <input
                type="number"
                name="defaultCommissionRate"
                value={formData.defaultCommissionRate}
                onChange={handleInputChange}
                className={`flex-1 p-3 border rounded-md ${
                  hasFieldError(validationErrors, 'defaultCommissionRate')
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-secondary focus:ring-secondary-200'
                }`}
                step="0.1"
                min="0"
                max="100"
              />
              <select
                name="defaultCommissionType"
                value={formData.defaultCommissionType}
                onChange={handleInputChange}
                className="p-3 border rounded-md"
              >
                <option value="percentage">%</option>
                <option value="fixed">₦</option>
              </select>
            </div>
            {getFieldError(validationErrors, 'defaultCommissionRate') && (
              <p className="mt-1 text-sm text-red-600">
                {getFieldError(validationErrors, 'defaultCommissionRate')}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={() => handleSubmit('commission')}
            className="px-6 py-3 bg-secondary text-white rounded-md hover:bg-secondary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || isSubmitting}
          >
            {loading || isSubmitting ? 'Updating...' : 'Update Commission'}
          </button>
        </div>
      </div>

      {/* Delivery Fees Section */}
      <div className="bg-white rounded-lg p-6 border">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full border">
              <span className="text-lg font-bold">🚚</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Delivery Fees</h3>
              <p className="text-sm text-gray-500">Delivery pricing configuration</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Base Fee (₦)</label>
            <input
              type="number"
              name="defaultBaseFee"
              value={formData.defaultBaseFee}
              onChange={handleInputChange}
              className={`w-full p-3 border rounded-md ${
                hasFieldError(validationErrors, 'defaultBaseFee')
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:border-secondary focus:ring-secondary-200'
              }`}
              min="0"
            />
            {getFieldError(validationErrors, 'defaultBaseFee') && (
              <p className="mt-1 text-sm text-red-600">
                {getFieldError(validationErrors, 'defaultBaseFee')}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Fee per KM (₦)</label>
            <input
              type="number"
              name="defaultFeePerKM"
              value={formData.defaultFeePerKM}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-md"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Free Distance (KM)</label>
            <input
              type="number"
              name="defaultFreeDistance"
              value={formData.defaultFreeDistance}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-md"
              min="0"
            />
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={() => handleSubmit('delivery')}
            className="px-6 py-3 bg-secondary text-white rounded-md hover:bg-secondary-dark transition-colors"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Delivery Fees'}
          </button>
        </div>
      </div>

      {/* Service Charges Section */}
      <div className="bg-white rounded-lg p-6 border">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full border">
              <span className="text-lg font-bold">⚡</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Service Charges</h3>
              <p className="text-sm text-gray-500">Additional Charges</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Charge Type</label>
            <select
              name="defaultServiceChargeType"
              value={formData.defaultServiceChargeType}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-md"
            >
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed Amount (₦)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Percentage (%)</label>
            <input
              type="number"
              name="defaultServiceChargeValue"
              value={formData.defaultServiceChargeValue}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-md"
              step="0.1"
              min="0"
            />
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={() => handleSubmit('service')}
            className="px-6 py-3 bg-secondary text-white rounded-md hover:bg-secondary-dark transition-colors"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Service Charge'}
          </button>
        </div>
      </div>

      {/* Fee Preview Section */}
      <div className="bg-white rounded-lg p-6 border">
        <h3 className="text-lg font-semibold mb-4">Fee Preview</h3>
        <p className="text-sm text-gray-500 mb-6">Preview how current settings affect order pricing</p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-md text-center">
            <h4 className="font-medium">Commission Rate</h4>
            <p className="text-2xl font-bold text-secondary">{preview.commissionRate}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-md text-center">
            <h4 className="font-medium">Base Delivery Fee</h4>
            <p className="text-2xl font-bold text-secondary">{preview.baseDeliveryFee}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-md text-center">
            <h4 className="font-medium">Per KM Fee</h4>
            <p className="text-2xl font-bold text-secondary">{preview.perKMFee}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-md text-center">
            <h4 className="font-medium">Service Charge</h4>
            <p className="text-2xl font-bold text-secondary">{preview.serviceCharge}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}
    </div>
  );
};

export default GlobalSettings;
